import {ref} from 'vue';
import type {AuthCompleteResponseDto} from '@/features/auth/api/auth';

export type IdentShortPollPhase = 'idle' | 'waiting' | 'aborted' | 'failed' | 'succeeded';

/**
 *  Result of polling /api/app/auth/{method}/complete (Mobile-ID + Smart-ID, login + binding + verify).
 *  - `data` populated on HTTP 200 with the camelCase `AuthCompleteResponseDto` body.
 *  - `errorCode` populated when the server returned a 4xx with `{errorCode}` body.
 *  - `aborted` flips when the caller invoked abort() (or started a new run) before a terminal result.
 *  - `networkError` flips when the ceiling was hit or too many consecutive probes failed.
 */
export interface IdentShortPollResult {
    data?: AuthCompleteResponseDto;
    errorCode?: string;
    aborted?: boolean;
    networkError?: boolean;
}

export interface IdentShortPollHandle {
    /** Reactive phase the UI binds to so it can show the verification code, spinner, or error. */
    phase: ReturnType<typeof ref<IdentShortPollPhase>>;
    /** Optional last error code surfaced from the response body or a synthesized 'NetworkError'/'Aborted'/'TIMEOUT'. */
    errorCode: ReturnType<typeof ref<string | null>>;
    /** Drive the short-poll: re-issues the request until the server returns a terminal 200/4xx. */
    run: (url: string, init?: RequestInit) => Promise<IdentShortPollResult>;
    /** Cancel the in-flight short-poll; the run() promise resolves with aborted=true. */
    abort: () => void;
}

// Cadence between probes. Each browser→Palk probe drives one Palk→Merit short-poll, so this matches
// the 2 s server→Merit cadence the previous long-poll client used internally.
const POLL_INTERVAL_MS = 2000;
// Back-off after a dropped probe (e.g. iOS froze the tab during the Smart-ID app switch and tore the
// socket) before re-issuing — don't hammer a flaky connection.
const NETWORK_RETRY_MS = 1000;
// Whole-ceremony budget. A hair above Merit's ~120 s session so a server-reported timeout/expiry wins
// the race and surfaces as its own error code; this ceiling is only the backstop if the server never
// answers a terminal result.
const CEILING_MS = 150000;
// Give up with a network error only after this many *consecutive* failed probes — a single dropped
// socket when the tab resumes must not abort a ceremony the user actually completed in the app.
const MAX_CONSECUTIVE_NETWORK_ERRORS = 4;

/**
 *  Client-driven short poll shared by Mobile-ID and Smart-ID across login, binding, and step-up
 *  verification. Each tick issues one short POST to the *…/complete* endpoint:
 *    - HTTP 202 → ceremony still running, wait POLL_INTERVAL_MS and probe again.
 *    - HTTP 200 → done, resolve with the camelCase DTO body.
 *    - HTTP 4xx → terminal failure, resolve with the body's `errorCode`.
 *    - fetch threw → transient (tab was frozen, socket dropped); back off and retry within the
 *      ceiling, surfacing 'NetworkError' only after several consecutive failures.
 *
 *  Polling short requests (rather than holding one long-lived socket) is what makes this survive the
 *  mandatory switch to the authenticator app on mobile browsers: a frozen tab simply resumes the
 *  loop, and the next probe collects the now-confirmed session.
 */
export function useIdentShortPoll(): IdentShortPollHandle {
    const phase = ref<IdentShortPollPhase>('idle');
    const errorCode = ref<string | null>(null);

    let controller: AbortController | null = null;
    // Bumped on every abort()/new run() so an older loop that's mid-await can tell it has been
    // superseded and bail out without touching the newer run's state.
    let generation = 0;
    // Resolvers for in-flight sleeps, so abort() can wake them immediately instead of waiting out the
    // timer.
    let wakeups: Array<() => void> = [];

    function wakeAll() {
        const pending = wakeups;
        wakeups = [];
        pending.forEach(fn => fn());
    }

    function abort() {
        generation++;
        if (controller) {
            controller.abort();
            controller = null;
        }
        wakeAll();
    }

    // Resolves after `ms`, or early if the run is superseded/aborted. Never rejects.
    function sleep(ms: number): Promise<void> {
        return new Promise(resolve => {
            const timer = setTimeout(() => {
                wakeups = wakeups.filter(w => w !== wake);
                resolve();
            }, ms);
            const wake = () => {
                clearTimeout(timer);
                resolve();
            };
            wakeups.push(wake);
        });
    }

    async function run(url: string, init: RequestInit = {}): Promise<IdentShortPollResult> {
        abort();
        const myGeneration = ++generation;
        const isActive = () => myGeneration === generation;

        phase.value = 'waiting';
        errorCode.value = null;

        const deadline = Date.now() + CEILING_MS;
        let consecutiveNetworkErrors = 0;

        while (isActive()) {
            if (Date.now() >= deadline) {
                phase.value = 'failed';
                errorCode.value = 'TIMEOUT';
                return {errorCode: 'TIMEOUT'};
            }

            // While the tab is hidden the browser may freeze timers and kill sockets; don't burn the
            // retry budget probing — wait and re-check. A resumed tab continues the loop here.
            if (typeof document !== 'undefined' && document.hidden) {
                await sleep(POLL_INTERVAL_MS);
                continue;
            }

            const myController = new AbortController();
            controller = myController;

            let response: Response;
            try {
                response = await fetch(url, {...init, signal: myController.signal});
            } catch {
                if (!isActive() || myController.signal.aborted) {
                    phase.value = 'aborted';
                    errorCode.value = 'Aborted';
                    return {aborted: true};
                }
                consecutiveNetworkErrors++;
                if (consecutiveNetworkErrors >= MAX_CONSECUTIVE_NETWORK_ERRORS) {
                    phase.value = 'failed';
                    errorCode.value = 'NetworkError';
                    return {networkError: true};
                }
                await sleep(NETWORK_RETRY_MS);
                continue;
            } finally {
                if (controller === myController) {
                    controller = null;
                }
            }
            consecutiveNetworkErrors = 0;

            if (!isActive()) {
                phase.value = 'aborted';
                errorCode.value = 'Aborted';
                return {aborted: true};
            }

            // 202 Accepted: ceremony still in flight — poll again after the cadence.
            if (response.status === 202) {
                await sleep(POLL_INTERVAL_MS);
                continue;
            }

            if (response.ok) {
                try {
                    const data = (await response.json()) as AuthCompleteResponseDto;
                    phase.value = 'succeeded';
                    return {data};
                } catch {
                    phase.value = 'failed';
                    errorCode.value = 'NetworkError';
                    return {networkError: true};
                }
            }

            try {
                const body = (await response.json()) as { errorCode?: string };
                phase.value = 'failed';
                errorCode.value = body?.errorCode ?? null;
                return {errorCode: body?.errorCode};
            } catch {
                phase.value = 'failed';
                errorCode.value = 'NetworkError';
                return {networkError: true};
            }
        }

        phase.value = 'aborted';
        errorCode.value = 'Aborted';
        return {aborted: true};
    }

    return {phase, errorCode, run, abort};
}
